import { t, tp, N_, getIntlLocale } from '../i18n/i18n';
/**
 * The end of a round, on the same plaque the dungeon's own ending uses.
 *
 * User (2026-09-06): *"In PvP mode, we need to make a more polished
 * game-ending screen, similar to how it is currently implemented in the PvE
 * dungeon mode."*
 *
 * What was here: a word in the middle of a dim screen, a sentence under it,
 * two figures, and a button — no card, no frame, no picture. Beside
 * `run-end-overlay.ts`, which had spent two passes learning that an
 * extraction game settles its whole proposition on this screen, the arena's
 * ending read as a placeholder that shipped.
 *
 * So it is now built to the same four questions that overlay answers, in the
 * same order, in the same stone card:
 *
 * 1. *What happened?* — the verdict, over its own picture.
 * 2. *What did I do?* — kills and damage, which the round already knew and
 *    never said. "DEFEATED" with nothing under it is a scoreboard with no
 *    score.
 * 3. *How did it end?* — how long it ran, and who was left standing.
 * 4. *What now?* — buttons, never a timer.
 *
 * ## The plates
 *
 * `tools/render-run-end-plate.py` builds them from the pack the arena is made
 * of — it runs on `DungeonInstanceScene`, the same room builder as the
 * dungeon, so this is literally the floor the round was fought on. The rhyme
 * defeat and extraction have on one candle, these three have on one sword:
 * planted upright and lit for a win, face-down in the dirt for a loss, and
 * two of them crossed with nobody's hand on either for a draw.
 */
export type ArenaPersonalOutcome = 'WIN' | 'LOSE' | 'DRAW';

/** What the round is worth reporting about, all of it already known client-side. */
export interface ArenaRoundSummary {
  /** Fighters still standing when the bell went. */
  survivors?: number;
  roundLengthS?: number;
  /** Opponents the local player put down themselves. */
  kills?: number;
  /** Damage the local player dealt, summed from `combat.result`. */
  damage?: number;
  /** Whether the local player was still alive at the end. A team can win without you. */
  survived?: boolean;
}

const PLATE_ART: Record<ArenaPersonalOutcome, string> = {
  WIN: '/assets/ui/run-end/arena_victory.webp',
  LOSE: '/assets/ui/run-end/arena_defeat.webp',
  DRAW: '/assets/ui/run-end/arena_draw.webp',
};

const COPY: Record<ArenaPersonalOutcome, { title: string; color: string }> = {
  WIN: { title: N_('Victory'), color: 'var(--color-success)' },
  LOSE: { title: N_('Defeated'), color: 'var(--color-error)' },
  DRAW: { title: N_('Draw'), color: 'var(--color-text-dim)' },
};

/**
 * The one line under the verdict, written as an account rather than a label.
 *
 * A win you were not alive for is a different sentence from one you finished
 * on your feet, and the arena is a team mode — saying so is the difference
 * between a result and a scoreboard row.
 */
export function outcomeSentence(outcome: ArenaPersonalOutcome, summary: ArenaRoundSummary = {}): string {
  switch (outcome) {
    case 'WIN':
      return summary.survived === false
        ? t('Your team took the pit. You did not live to see it.')
        : t('Your team is the last one standing.');
    case 'DRAW':
      return t('Every team went down together. The pit keeps the round.');
    default:
      return summary.survived
        ? t('Another team outlasted yours.')
        : t('You went down, and your team went with you.');
  }
}

export class ArenaRoundOverlay {
  private readonly root: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private readonly head: HTMLDivElement;
  private readonly headTitleSlot: HTMLDivElement;
  private readonly plates: Record<ArenaPersonalOutcome, HTMLImageElement>;
  /** Which plates failed to load. A missing file costs its own outcome the picture and nothing else. */
  private readonly brokenPlates = new Set<string>();
  private readonly titleEl: HTMLDivElement;
  private readonly detailEl: HTMLDivElement;
  private readonly statsEl: HTMLDivElement;
  private readonly primaryBtn: HTMLButtonElement;
  private readonly secondaryBtn: HTMLButtonElement;

  /**
   * Arena rework (2026-08-26) — the results screen had no way out.
   *
   * It was `pointer-events-none` with no buttons: the only thing that ended it
   * was the room disconnecting itself five seconds later.
   */
  onReturnToHub: () => void = () => { window.location.href = '/play'; };
  onQueueAgain: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // `items-start` + `my-auto` on the card, for the reason run-end-overlay.ts
    // records: a centred flex item taller than its scroll container has its
    // own top clipped and unreachable, which on a phone in landscape hides the
    // verdict itself.
    this.root.className = 'fixed inset-0 z-[1200] hidden items-start justify-center '
      + 'scrim px-4 py-6 overflow-y-auto select-none';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', t('Round summary'));

    this.card = document.createElement('div');
    this.card.className = 'w-full max-w-[30rem] my-auto frame-raised overflow-hidden '
      + 'px-5 py-5 flex flex-col gap-4 text-center';

    // Negative margins exactly cancel the card's own px-5/pt-5, so the art
    // bleeds to the plaque's rounded edge without a second element that has to
    // be kept in sync with the padding.
    this.head = document.createElement('div');
    this.head.className = 'relative -mx-5 -mt-5 mb-1 hidden';

    const makePlate = (outcome: ArenaPersonalOutcome): HTMLImageElement => {
      const img = document.createElement('img');
      img.src = PLATE_ART[outcome];
      // Decorative: the sentence below says what happened in words, and a
      // screen reader describing a floor would only delay it.
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.width = 960;
      img.height = 530;
      img.decoding = 'async';
      img.draggable = false;
      img.className = 'w-full aspect-[960/530] max-h-[34vh] sm:max-h-[46vh] '
        + 'object-cover object-[50%_62%] select-none animate-flicker hidden';
      // A missing file costs the screen its picture and nothing else: the
      // title goes back where it was and the plaque reads as it did before
      // there was any art.
      img.addEventListener('error', () => {
        this.brokenPlates.add(PLATE_ART[outcome]);
        if (!img.classList.contains('hidden')) {
          this.head.classList.add('hidden');
          this.card.insertBefore(this.titleEl, this.detailEl);
        }
      });
      return img;
    };
    this.plates = { WIN: makePlate('WIN'), LOSE: makePlate('LOSE'), DRAW: makePlate('DRAW') };

    // The lobby tiles' own gradient — text over generated art is solved once
    // in this codebase and should not be solved twice.
    const wash = document.createElement('div');
    wash.className = 'absolute inset-0 tile-scrim';
    const lid = document.createElement('div');
    lid.className = 'absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/60 to-transparent';

    this.headTitleSlot = document.createElement('div');
    this.headTitleSlot.className = 'absolute inset-x-0 bottom-0 px-4 pb-3';

    this.head.append(this.plates.WIN, this.plates.LOSE, this.plates.DRAW, wash, lid, this.headTitleSlot);

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'font-heading font-black text-[clamp(2.2rem,8vw,3.6rem)] leading-none';
    this.titleEl.style.textShadow = '0 2px 16px rgba(0,0,0,0.95), 0 0 32px rgba(0,0,0,0.75)';

    this.detailEl = document.createElement('div');
    this.detailEl.className = 'text-text-dim text-[clamp(0.9rem,2.4vw,1.05rem)] leading-snug';

    this.statsEl = document.createElement('div');
    this.statsEl.className = 'flex justify-center gap-6 flex-wrap eyebrow eyebrow-dim '
      + 'border-y border-[#4a3c2d]/70 py-2';

    const buttons = document.createElement('div');
    buttons.className = 'flex flex-col sm:flex-row gap-2 pt-1';

    this.primaryBtn = document.createElement('button');
    this.primaryBtn.type = 'button';
    this.primaryBtn.className = 'btn btn-gold flex-[1.6]';
    this.primaryBtn.textContent = t('Back to the Sconce');
    this.primaryBtn.addEventListener('click', () => { this.hide(); this.onReturnToHub(); });

    this.secondaryBtn = document.createElement('button');
    this.secondaryBtn.type = 'button';
    this.secondaryBtn.className = 'btn btn-stone flex-1 hidden';
    this.secondaryBtn.textContent = t('Queue Again');
    this.secondaryBtn.addEventListener('click', () => { this.hide(); this.onQueueAgain?.(); });

    buttons.append(this.primaryBtn, this.secondaryBtn);
    this.card.append(this.head, this.titleEl, this.detailEl, this.statsEl, buttons);
    this.root.appendChild(this.card);
    container.appendChild(this.root);
  }

  show(outcome: ArenaPersonalOutcome, summary: ArenaRoundSummary = {}): void {
    const { title, color } = COPY[outcome];
    this.titleEl.textContent = t(title);
    this.titleEl.style.color = color;
    this.detailEl.textContent = outcomeSentence(outcome, summary);

    // The verdict goes *on* the picture, not above it — the same rule the PvE
    // plaque keeps: the words and the floor they describe are one image.
    const useArt = !this.brokenPlates.has(PLATE_ART[outcome]);
    for (const key of ['WIN', 'LOSE', 'DRAW'] as const) {
      this.plates[key].classList.toggle('hidden', !(useArt && key === outcome));
    }
    this.head.classList.toggle('hidden', !useArt);
    if (useArt) this.headTitleSlot.appendChild(this.titleEl);
    else this.card.insertBefore(this.titleEl, this.detailEl);

    // What you did comes before what happened around you: a fight screen that
    // reports only the room's state is a screen about somebody else.
    const cells: HTMLDivElement[] = [];
    if (summary.kills !== undefined) cells.push(stat(summary.kills.toLocaleString(getIntlLocale()), tp(summary.kills, 'kill', 'kills')));
    if (summary.damage !== undefined) cells.push(stat(Math.round(summary.damage).toLocaleString(getIntlLocale()), t('damage')));
    if (summary.roundLengthS !== undefined) cells.push(stat(formatDuration(summary.roundLengthS), t('round')));
    if (summary.survivors !== undefined) cells.push(stat(String(summary.survivors), t('left standing')));
    this.statsEl.replaceChildren(...cells);
    this.statsEl.classList.toggle('hidden', cells.length === 0);

    this.secondaryBtn.classList.toggle('hidden', this.onQueueAgain === null);
    this.root.classList.remove('hidden');
    this.root.classList.add('flex');
  }

  hide(): void {
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
  }

  dispose(): void {
    this.root.remove();
  }
}

/** One figure and its label, matching the PvE summary's ledger row exactly. */
function stat(value: string, label: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center';
  const v = document.createElement('span');
  v.className = 'text-base font-semibold text-text normal-case tracking-normal';
  v.textContent = value;
  const l = document.createElement('span');
  l.textContent = label;
  el.append(v, l);
  return el;
}

/** `1m 42s`, or `42s` under a minute — a duration, not a clock. */
export function formatDuration(totalSeconds: number): string {
  const clamped = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return minutes > 0 ? t('{m}m {s}s', { m: minutes, s: seconds }) : t('{s}s', { s: seconds });
}
